import { useEffect, useMemo, useState, type ReactNode } from 'react';

import { BANK_ASSIGNABLE_ROLES, toDisplayRole } from '@/features/auth/roles';
import { useToast } from '@/features/campaigns/toast-context';

import { formatBatch, newRule } from './admin-format';
import { EligibilityBuilder } from './EligibilityBuilder';
import {
  DOMAIN_RE,
  TRIGGER_CATALOG,
  formatBytes,
  formatTriggerValue,
  formatUploadedDate,
  parseConfig,
  serializeIntelligence,
  serializeProducts,
  type TriggerConfig,
} from './manage-config';
import {
  useActivateTenant,
  useCloseTenant,
  useConfigureTenantIdp,
  useReplaceRoleMappings,
  useRoleMappings,
  useTenantDetail,
  useUpdateTenantConfig,
} from './useTenants';
import { MAPPABLE_STORED_ROLES } from './admin-api';
import type {
  OnboardingProduct,
  RoleMapping,
  TeamUser,
  TenantCard,
  TenantConfigPatch,
  TenantDetail,
  TenantIdpConfig,
  VaultDocument,
} from './admin-api';

const CLOSE_REASONS = [
  'Contract ended',
  'Customer request',
  'Non-payment',
  'Compliance / regulatory',
  'Other',
] as const;

const PRODUCT_CATEGORIES = ['Lending', 'Deposits', 'Savings', 'Cards'] as const;

/** v7 generateConfirmPhrase equivalent: a short deterministic-per-mount phrase. */
function makeConfirmPhrase(bankName: string): string {
  const slug = bankName.toLowerCase().split(/\s+/)[0] ?? 'account';
  return `close ${slug}`;
}

/** v7 "Activated" read value, from the real activatedAt (— when the tenant is not yet active). */
function formatActivated(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

/**
 * Manage Account (SCRUM-433, v7 renderManageAccount): the sectioned customer
 * profile — Organization Details, Email Domains, SSO / Identity Provider,
 * Role Mapping (SCRUM-585), Team & Roles, Intelligence Triggers, Products,
 * Vault — each with an edit-pencil toggle revealing an inline editor, plus
 * the Activate & provision panel (inactive tenants) and the understated
 * Close Account danger flow.
 *
 * Data is REAL (GET /api/admin/tenants/{id}): org fields + team users +
 * vault documents from the DB, and the three config-backed sections
 * (triggers/products) parsed from the stored onboarding_config JSON.
 *
 * PERSISTENCE (plan Story B decision):
 *  - Organization Details, Intelligence Triggers, Products
 *    PERSIST via PATCH /api/admin/tenants/{id}/config.
 *  - Team & Roles and Vault editors are SESSION-LOCAL ONLY — there is no admin
 *    user-management or vault-mutation backend. They are boldly labeled demo-era
 *    in the UI (see the amber note in each) and in ADMIN.md. No fake backend.
 */
export function ManageAccountPage({ tenant, onBack }: { tenant: TenantCard; onBack: () => void }) {
  const { detail, isLoading, isError } = useTenantDetail(tenant.id);

  return (
    <div className="min-h-full p-4" data-testid="manage-account-page">
      {/* Breadcrumb (v7: Customers / <bank>). Page title/subtitle live in the app chrome. */}
      <nav aria-label="Breadcrumb" className="mb-6 flex items-center gap-2 text-[0.8125rem]">
        <button type="button" onClick={onBack} className="text-brand-primary hover:underline">
          Customers
        </button>
        <span className="text-text-3">/</span>
        <span className="text-text-0 font-medium">{tenant.bankName}</span>
      </nav>

      {isLoading ? (
        <div className="text-text-3 py-16 text-center text-sm">Loading account…</div>
      ) : isError || !detail ? (
        <div className="text-text-3 py-16 text-center text-sm">
          This account&rsquo;s profile is unreachable right now — try again shortly.
        </div>
      ) : (
        <ManageAccountBody tenantId={tenant.id} detail={detail} onBack={onBack} />
      )}
    </div>
  );
}

function ManageAccountBody({
  tenantId,
  detail,
  onBack,
}: {
  tenantId: string;
  detail: TenantDetail;
  onBack: () => void;
}) {
  const config = useMemo(
    () => parseConfig(detail.onboardingConfigJson),
    [detail.onboardingConfigJson],
  );

  // SCRUM-585: the activate mutation + its toasts live HERE (not in
  // ActivationPanel) because the panel unmounts as a result of its own
  // success — mutate-level callbacks on an unmounted component are skipped
  // (see the ActivationPanel doc comment).
  const { showToast } = useToast();
  const activate = useActivateTenant(tenantId);
  const handleActivate = () => {
    activate.mutate(undefined, {
      onSuccess: () => {
        showToast({
          title: 'Account activated',
          body: `${detail.bankName} is provisioned — its users can now sign in.`,
          variant: 'success',
        });
      },
      onError: (e) => {
        showToast({ title: 'Activation failed', body: e.message });
      },
    });
  };

  return (
    <>
      <OrgDetailsSection tenantId={tenantId} detail={detail} />
      {detail.status === 'inactive' && (
        <ActivationPanel detail={detail} pending={activate.isPending} onActivate={handleActivate} />
      )}
      <EmailDomainsSection tenantId={tenantId} domains={detail.emailDomains} />
      <SsoSection tenantId={tenantId} detail={detail} />
      <RoleMappingsSection tenantId={tenantId} />
      <TeamRolesSection team={detail.team} />
      <TriggersSection tenantId={tenantId} intelligence={config.intelligence} />
      <ProductsSection tenantId={tenantId} products={config.products} />
      <VaultSection vault={detail.vault} />
      <CloseAccountPanel tenantId={tenantId} bankName={detail.bankName} onClosed={onBack} />
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Shared section shell (v7 .manage-section + header + pencil toggle) */
/* ------------------------------------------------------------------ */

function ManageSection({
  title,
  subtitle,
  children,
  editContent,
  ariaLabel,
}: {
  title: string;
  subtitle: string;
  children: ReactNode;
  /** When provided, renders the edit pencil; called with a close() to exit edit mode. */
  editContent?: (close: () => void) => ReactNode;
  ariaLabel?: string;
}) {
  const [editing, setEditing] = useState(false);
  const close = () => {
    setEditing(false);
  };

  return (
    <section
      aria-label={ariaLabel ?? title}
      className={`bg-bg-4 rounded-tk-lg mb-4 border p-6 transition-colors ${
        editing ? 'border-[color-mix(in_srgb,var(--cyan)_25%,var(--border-0))]' : 'border-border-0'
      }`}
    >
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h2 className="font-display text-text-0 text-[0.9375rem] font-semibold">{title}</h2>
          <p className="text-text-2 mt-0.5 text-xs">{subtitle}</p>
        </div>
        {editContent && (
          <button
            type="button"
            aria-label={editing ? `Close ${title} editor` : `Edit ${title}`}
            title={editing ? 'Close' : 'Edit'}
            onClick={() => {
              setEditing((e) => !e);
            }}
            className={`rounded-tk-sm grid h-[30px] w-[30px] shrink-0 place-items-center border transition-colors ${
              editing
                ? 'border-[color-mix(in_srgb,var(--cyan)_30%,transparent)] bg-[var(--cyan-dim)] text-[var(--cyan)]'
                : 'text-text-3 hover:text-text-0 hover:bg-bg-2 hover:border-border-1 border-transparent'
            }`}
          >
            {editing ? <CloseIcon /> : <PencilIcon />}
          </button>
        )}
      </div>
      {editing && editContent ? editContent(close) : children}
    </section>
  );
}

/* ---------------------------- Section 1: Organization Details ---------------------------- */

const STATUS_PILL: Record<TenantDetail['status'], string> = {
  active: 'text-[var(--success)] bg-[var(--success-dim)]',
  inactive: 'text-text-3 bg-bg-2',
  closed: 'text-[var(--danger)] bg-[var(--danger-dim)]',
};

function OrgDetailsSection({ tenantId, detail }: { tenantId: string; detail: TenantDetail }) {
  const { showToast } = useToast();
  const update = useUpdateTenantConfig(tenantId);

  return (
    <ManageSection
      title="Organization Details"
      subtitle="Core account information and status"
      editContent={(close) => (
        <OrgDetailsEditor
          detail={detail}
          pending={update.isPending}
          onSave={(patch) => {
            update.mutate(patch, {
              onSuccess: () => {
                showToast({
                  title: 'Saved',
                  body: 'Organization details updated.',
                  variant: 'success',
                });
                close();
              },
              onError: (e) => {
                showToast({ title: 'Save failed', body: e.message });
              },
            });
          }}
          onCancel={close}
        />
      )}
    >
      <dl className="grid grid-cols-2 gap-x-8 gap-y-4 md:grid-cols-3">
        <ReadField label="Account Name" value={detail.bankName} />
        <div className="flex flex-col gap-1">
          <dt className="text-text-3 font-mono text-[0.6875rem] font-semibold tracking-[0.03em] uppercase">
            Status
          </dt>
          <dd>
            <span
              className={`rounded-full px-2 py-0.5 font-mono text-[0.625rem] font-bold tracking-[0.04em] uppercase ${STATUS_PILL[detail.status]}`}
            >
              {detail.status}
            </span>
          </dd>
        </div>
        <ReadField label="Charter" value={detail.charter ?? '—'} />
        <ReadField label="Region" value={detail.region ?? '—'} />
        <ReadField label="Last Data Batch" value={formatBatch(detail.lastBatchAt)} />
        <ReadField label="Activated" value={formatActivated(detail.activatedAt)} />
      </dl>
    </ManageSection>
  );
}

function OrgDetailsEditor({
  detail,
  pending,
  onSave,
  onCancel,
}: {
  detail: TenantDetail;
  pending: boolean;
  onSave: (patch: TenantConfigPatch) => void;
  onCancel: () => void;
}) {
  const [bankName, setBankName] = useState(detail.bankName);
  const [charter, setCharter] = useState(detail.charter ?? '');
  const [region, setRegion] = useState(detail.region ?? '');

  return (
    <div>
      <div className="grid max-w-[560px] grid-cols-1 gap-4 md:grid-cols-2">
        <Field label="Account Name">
          <input
            aria-label="Account Name"
            value={bankName}
            onChange={(e) => {
              setBankName(e.target.value);
            }}
            className={inputCls}
          />
        </Field>
        <Field label="Charter">
          <input
            aria-label="Charter"
            value={charter}
            onChange={(e) => {
              setCharter(e.target.value);
            }}
            className={inputCls}
          />
        </Field>
        <Field label="Region">
          <input
            aria-label="Region"
            value={region}
            onChange={(e) => {
              setRegion(e.target.value);
            }}
            className={inputCls}
          />
        </Field>
      </div>
      <EditorFooter
        pending={pending}
        onCancel={onCancel}
        onSave={() => {
          onSave({ bankName, charter, region });
        }}
      />
    </div>
  );
}

/* ---------------------------- Section 1b: Email Domains (persist, SCRUM-575) ---------------------------- */

function EmailDomainsSection({ tenantId, domains }: { tenantId: string; domains: string[] }) {
  const { showToast } = useToast();
  const update = useUpdateTenantConfig(tenantId);

  return (
    <ManageSection
      title="Email Domains"
      subtitle="Users with these email domains are routed to this bank's sign-in"
      editContent={(close) => (
        <EmailDomainsEditor
          domains={domains}
          pending={update.isPending}
          onSave={(next) => {
            update.mutate(
              { emailDomains: next },
              {
                onSuccess: () => {
                  showToast({ title: 'Saved', body: 'Email domains updated.', variant: 'success' });
                  close();
                },
                onError: (e) => {
                  showToast({ title: 'Save failed', body: e.message });
                },
              },
            );
          }}
          onCancel={close}
        />
      )}
    >
      {domains.length === 0 ? (
        <p className="text-text-3 text-sm">
          No email domains configured. Add one so this bank&apos;s users can sign in.
        </p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {domains.map((d) => (
            <span
              key={d}
              className="rounded-tk-sm bg-bg-2 text-text-1 border-border-0 border px-2.5 py-1 font-mono text-xs"
            >
              {d}
            </span>
          ))}
        </div>
      )}
    </ManageSection>
  );
}

function EmailDomainsEditor({
  domains,
  pending,
  onSave,
  onCancel,
}: {
  domains: string[];
  pending: boolean;
  onSave: (next: string[]) => void;
  onCancel: () => void;
}) {
  const [list, setList] = useState<string[]>(domains);
  const [input, setInput] = useState('');
  const [error, setError] = useState<string | null>(null);

  function addDomain() {
    const candidate = input.trim().toLowerCase();
    if (candidate.length === 0) {
      return;
    }
    if (!DOMAIN_RE.test(candidate)) {
      setError('Enter a bare domain, e.g. bank.com');
      return;
    }
    if (list.includes(candidate)) {
      setError('That domain is already in the list.');
      return;
    }
    setList((prev) => [...prev, candidate]);
    setInput('');
    setError(null);
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap gap-2">
        {list.map((d) => (
          <span
            key={d}
            className="rounded-tk-sm bg-bg-2 text-text-1 border-border-0 flex items-center gap-1.5 border px-2.5 py-1 font-mono text-xs"
          >
            {d}
            <button
              type="button"
              aria-label={`Remove ${d}`}
              onClick={() => {
                setList((prev) => prev.filter((x) => x !== d));
              }}
              className="text-text-3 hover:text-[var(--danger)]"
            >
              <CloseIcon />
            </button>
          </span>
        ))}
        {list.length === 0 && <span className="text-text-3 text-xs">No domains yet.</span>}
      </div>

      <div className="flex flex-col gap-1">
        <div className="flex gap-2">
          <input
            className="bg-bg-1 border-border-0 rounded-tk-sm text-text-0 flex-1 border px-2.5 py-1.5 font-mono text-sm"
            placeholder="bank.com"
            value={input}
            onChange={(e) => {
              setInput(e.target.value);
              if (error) setError(null);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addDomain();
              }
            }}
            aria-invalid={!!error}
            aria-label="Add email domain"
          />
          <button
            type="button"
            onClick={addDomain}
            className="rounded-tk-sm border-border-0 text-text-1 hover:bg-bg-2 border px-3 py-1.5 text-sm"
          >
            Add
          </button>
        </div>
        {error && (
          <span role="alert" className="text-xs text-[var(--danger)]">
            {error}
          </span>
        )}
      </div>

      <EditorFooter
        pending={pending}
        onCancel={onCancel}
        onSave={() => {
          onSave(list);
        }}
      />
    </div>
  );
}

/* ---------------------------- Section 1c: Activation (SCRUM-585) ---------------------------- */

/**
 * The explicit "Activate & provision" action (plan Q2). Rendered only while
 * the tenant is inactive (Draft/Provisioning). Client-side readiness hints
 * mirror the server's FAIL-CLOSED validation (IdP config + >=1 email domain),
 * but the server remains the authority — its 400 message surfaces verbatim.
 *
 * UNMOUNT-RACE NOTE (found by the SCRUM-585 provisioning e2e in a real
 * browser): this panel unmounts AS A RESULT of its own mutation (the status
 * flips to active and the conditional render drops it). TanStack Query skips
 * mutate-level callbacks once the observing component is unmounted, so the
 * success toast silently never fired. The mutation therefore lives in
 * ManageAccountBody (which stays mounted through the transition) and is
 * passed down as props — do not move it back into this component.
 */
function ActivationPanel({
  detail,
  pending,
  onActivate,
}: {
  detail: TenantDetail;
  pending: boolean;
  onActivate: () => void;
}) {
  const idpReady = Boolean(
    detail.idpDiscoveryUrl && detail.idpClientId && detail.idpClientSecretRef,
  );
  const domainsReady = detail.emailDomains.length > 0;
  // SCRUM-594: the seeded primary-contact user is the only loginnable identity
  // right after activation — their email domain must be routed. Mirrors the
  // server's fail-closed rule; when the contact email is unknown (older API
  // payload) the hint is omitted and the server remains the authority.
  const contactDomain = detail.primaryContactEmail?.trim().toLowerCase().split('@').pop() ?? null;
  const contactRouted = contactDomain === null || detail.emailDomains.includes(contactDomain);
  const ready = idpReady && domainsReady && contactRouted;

  return (
    <section
      aria-label="Activate Account"
      className="rounded-tk-lg mb-4 border border-[color-mix(in_srgb,var(--cyan)_25%,var(--border-0))] bg-[var(--cyan-dim)] p-6"
    >
      <div className="flex items-center justify-between gap-4">
        <div>
          <h2 className="font-display text-text-0 text-[0.9375rem] font-semibold">
            This account is not active yet
          </h2>
          <p className="text-text-2 mt-0.5 text-xs">
            Activating provisions the bank&rsquo;s workspace and enables sign-in for its users.
          </p>
          <ul className="text-text-2 mt-2 flex flex-col gap-1 text-xs">
            <li className={idpReady ? 'text-[var(--success)]' : 'text-text-3'}>
              {idpReady ? '✓' : '○'} SSO / identity provider configured
            </li>
            <li className={domainsReady ? 'text-[var(--success)]' : 'text-text-3'}>
              {domainsReady ? '✓' : '○'} At least one login email domain
            </li>
            {contactDomain !== null && (
              <li className={contactRouted ? 'text-[var(--success)]' : 'text-text-3'}>
                {contactRouted ? '✓' : '○'} Primary contact email on a login domain ({contactDomain}
                )
              </li>
            )}
          </ul>
        </div>
        <button
          type="button"
          disabled={!ready || pending}
          title={ready ? undefined : 'Complete the SSO and email-domain sections first'}
          onClick={onActivate}
          className="bg-brand-primary text-bg-0 rounded-tk-md shrink-0 px-4 py-2 text-[0.8125rem] font-semibold disabled:cursor-not-allowed disabled:opacity-40"
        >
          {pending ? 'Activating…' : 'Activate & provision'}
        </button>
      </div>
    </section>
  );
}

/* ---------------------------- Section 1d: SSO / Identity Provider (SCRUM-585) ---------------------------- */

function SsoSection({ tenantId, detail }: { tenantId: string; detail: TenantDetail }) {
  const { showToast } = useToast();
  const configure = useConfigureTenantIdp(tenantId);
  const configured = Boolean(detail.idpDiscoveryUrl);

  return (
    <ManageSection
      title="SSO / Identity Provider"
      subtitle="OIDC federation to the bank's own identity provider (ADR-011)"
      editContent={(close) => (
        <SsoEditor
          initial={{
            discoveryUrl: detail.idpDiscoveryUrl ?? '',
            clientId: detail.idpClientId ?? '',
            clientSecretRef: detail.idpClientSecretRef ?? '',
            scopes: detail.idpScopes ?? '',
          }}
          pending={configure.isPending}
          onSave={(idp) => {
            configure.mutate(idp, {
              onSuccess: () => {
                showToast({
                  title: 'Saved',
                  body: 'SSO configuration updated.',
                  variant: 'success',
                });
                close();
              },
              onError: (e) => {
                showToast({ title: 'Save failed', body: e.message });
              },
            });
          }}
          onCancel={close}
        />
      )}
    >
      {configured ? (
        <div className="grid grid-cols-2 gap-4">
          <ReadField label="Discovery URL" value={detail.idpDiscoveryUrl ?? '—'} />
          <ReadField label="Client ID" value={detail.idpClientId ?? '—'} />
          <ReadField
            label="Client secret (Secrets Manager name)"
            value={detail.idpClientSecretRef ?? '—'}
          />
          <ReadField label="Scopes" value={detail.idpScopes ?? 'openid profile email (default)'} />
        </div>
      ) : (
        <p className="text-text-3 text-sm">
          No identity provider configured. Add the bank&apos;s OIDC details so their team can sign
          in with SSO.
        </p>
      )}
    </ManageSection>
  );
}

function SsoEditor({
  initial,
  pending,
  onSave,
  onCancel,
}: {
  initial: { discoveryUrl: string; clientId: string; clientSecretRef: string; scopes: string };
  pending: boolean;
  onSave: (idp: TenantIdpConfig) => void;
  onCancel: () => void;
}) {
  const [discoveryUrl, setDiscoveryUrl] = useState(initial.discoveryUrl);
  const [clientId, setClientId] = useState(initial.clientId);
  const [clientSecretRef, setClientSecretRef] = useState(initial.clientSecretRef);
  const [scopes, setScopes] = useState(initial.scopes);
  const valid =
    discoveryUrl.trim().startsWith('https://') &&
    clientId.trim().length > 0 &&
    clientSecretRef.trim().length > 0;

  return (
    <div className="flex flex-col gap-3">
      <Field label="Discovery URL *">
        <input
          aria-label="IdP discovery URL"
          value={discoveryUrl}
          placeholder="https://idp.bank.com/.well-known/openid-configuration"
          onChange={(e) => {
            setDiscoveryUrl(e.target.value);
          }}
          className={inputCls}
        />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Client ID *">
          <input
            aria-label="IdP client ID"
            value={clientId}
            onChange={(e) => {
              setClientId(e.target.value);
            }}
            className={inputCls}
          />
        </Field>
        <Field label="Client secret name (Secrets Manager) *">
          <input
            aria-label="IdP client secret name"
            value={clientSecretRef}
            placeholder="e.g. bankiq-<bank>-client-secret"
            onChange={(e) => {
              setClientSecretRef(e.target.value);
            }}
            className={inputCls}
          />
        </Field>
      </div>
      <Field label="Scopes (optional)">
        <input
          aria-label="IdP scopes"
          value={scopes}
          placeholder="openid profile email"
          onChange={(e) => {
            setScopes(e.target.value);
          }}
          className={inputCls}
        />
      </Field>
      <p className="text-text-3 text-[0.6875rem]">
        Only the secret&rsquo;s NAME is stored — the secret value itself stays in IBM Secrets
        Manager.
      </p>
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="border-border-1 bg-bg-3 text-text-1 hover:text-text-0 rounded-tk-md border px-4 py-2 text-[0.8125rem] font-medium"
        >
          Cancel
        </button>
        <button
          type="button"
          disabled={!valid || pending}
          onClick={() => {
            onSave({
              discoveryUrl: discoveryUrl.trim(),
              clientId: clientId.trim(),
              clientSecretRef: clientSecretRef.trim(),
              scopes: scopes.trim() || undefined,
            });
          }}
          className="bg-brand-primary text-bg-0 rounded-tk-md px-4 py-2 text-[0.8125rem] font-semibold disabled:cursor-not-allowed disabled:opacity-40"
        >
          {pending ? 'Saving…' : 'Save SSO Configuration'}
        </button>
      </div>
    </div>
  );
}

/* ---------------------------- Section 1e: Role Mapping (SCRUM-585) ---------------------------- */

function RoleMappingsSection({ tenantId }: { tenantId: string }) {
  const { showToast } = useToast();
  const { mappings, isLoading, isError } = useRoleMappings(tenantId);
  const replace = useReplaceRoleMappings(tenantId);

  return (
    <ManageSection
      title="Role Mapping"
      subtitle="IdP group names mapped to BankIQ roles at sign-in"
      editContent={(close) => (
        <RoleMappingsEditor
          initial={mappings}
          pending={replace.isPending}
          onSave={(next) => {
            replace.mutate(next, {
              onSuccess: () => {
                showToast({ title: 'Saved', body: 'Role mappings updated.', variant: 'success' });
                close();
              },
              onError: (e) => {
                showToast({ title: 'Save failed', body: e.message });
              },
            });
          }}
          onCancel={close}
        />
      )}
    >
      {isLoading ? (
        <p className="text-text-3 text-sm">Loading role mappings…</p>
      ) : isError ? (
        <p className="text-text-3 text-sm">Role mappings are unreachable right now.</p>
      ) : mappings.length === 0 ? (
        <p className="text-text-3 text-sm">
          No role mappings yet. Users signing in without a mapped group keep their individually
          assigned role.
        </p>
      ) : (
        <ul className="flex flex-col gap-1.5">
          {mappings.map((m) => (
            <li
              key={m.idpGroupClaim}
              className="bg-bg-2 border-border-0 rounded-tk-md flex items-center justify-between border px-3 py-2"
            >
              <span className="text-text-0 font-mono text-xs">{m.idpGroupClaim}</span>
              <span className="text-text-2 text-xs">{toDisplayRole(m.grantedRole)}</span>
            </li>
          ))}
        </ul>
      )}
    </ManageSection>
  );
}

function RoleMappingsEditor({
  initial,
  pending,
  onSave,
  onCancel,
}: {
  initial: RoleMapping[];
  pending: boolean;
  onSave: (mappings: RoleMapping[]) => void;
  onCancel: () => void;
}) {
  const [list, setList] = useState<RoleMapping[]>(initial);
  const [claim, setClaim] = useState('');
  const [role, setRole] = useState<string>(MAPPABLE_STORED_ROLES[0]);

  function addMapping() {
    const trimmed = claim.trim();
    if (!trimmed || list.some((m) => m.idpGroupClaim === trimmed)) return;
    setList([...list, { idpGroupClaim: trimmed, grantedRole: role }]);
    setClaim('');
  }

  return (
    <div className="flex flex-col gap-3">
      {list.length > 0 && (
        <ul className="flex flex-col gap-1.5">
          {list.map((m, i) => (
            <li
              key={m.idpGroupClaim}
              className="bg-bg-2 border-border-0 rounded-tk-md flex items-center justify-between border px-3 py-2"
            >
              <span className="text-text-0 font-mono text-xs">{m.idpGroupClaim}</span>
              <span className="flex items-center gap-3">
                <span className="text-text-2 text-xs">{toDisplayRole(m.grantedRole)}</span>
                <button
                  type="button"
                  aria-label={`Remove mapping ${m.idpGroupClaim}`}
                  onClick={() => {
                    setList(list.filter((_, j) => j !== i));
                  }}
                  className="text-text-3 hover:text-danger text-xs"
                >
                  Remove
                </button>
              </span>
            </li>
          ))}
        </ul>
      )}
      <div className="flex items-center gap-2">
        <input
          aria-label="IdP group name"
          value={claim}
          placeholder="IdP group name (e.g. bank-executives)"
          onChange={(e) => {
            setClaim(e.target.value);
          }}
          className={inputCls}
        />
        <select
          aria-label="Mapped BankIQ role"
          value={role}
          onChange={(e) => {
            setRole(e.target.value);
          }}
          className={inputCls}
        >
          {MAPPABLE_STORED_ROLES.map((r) => (
            <option key={r} value={r}>
              {toDisplayRole(r)}
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={addMapping}
          className="border-border-1 bg-bg-3 text-text-1 hover:text-text-0 rounded-tk-md h-9 shrink-0 border px-3 text-xs font-medium"
        >
          Add
        </button>
      </div>
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="border-border-1 bg-bg-3 text-text-1 hover:text-text-0 rounded-tk-md border px-4 py-2 text-[0.8125rem] font-medium"
        >
          Cancel
        </button>
        <button
          type="button"
          disabled={pending}
          onClick={() => {
            onSave(list);
          }}
          className="bg-brand-primary text-bg-0 rounded-tk-md px-4 py-2 text-[0.8125rem] font-semibold disabled:cursor-not-allowed disabled:opacity-40"
        >
          {pending ? 'Saving…' : 'Save Role Mappings'}
        </button>
      </div>
    </div>
  );
}

/* ---------------------------- Section 2: Team & Roles (session-local) ---------------------------- */

function TeamRolesSection({ team }: { team: TeamUser[] }) {
  // Session-local demo-era editor state (plan RT1: no user-management backend).
  const [users, setUsers] = useState<TeamUser[]>(team);
  useEffect(() => {
    setUsers(team);
  }, [team]);

  const subtitle = `${String(users.length)} ${users.length === 1 ? 'user' : 'users'}`;

  return (
    <ManageSection
      title="Team & Roles"
      subtitle={subtitle}
      editContent={(close) => <TeamEditor users={users} onChange={setUsers} onDone={close} />}
    >
      {users.length === 0 ? (
        <EmptyState>No team members yet — users appear once the tenant is provisioned.</EmptyState>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-[0.8125rem]">
            <thead>
              <tr>
                <ManageTh>Name</ManageTh>
                <ManageTh>Email</ManageTh>
                <ManageTh>Role</ManageTh>
              </tr>
            </thead>
            <tbody>
              {users.map((u, i) => (
                <tr
                  key={`${u.email}-${String(i)}`}
                  className="border-border-0 border-b last:border-0"
                >
                  <td className="text-text-0 py-2 font-medium">{u.displayName}</td>
                  <td className="text-text-2 py-2 font-mono text-xs">{u.email}</td>
                  <td className="py-2">
                    <ManageTag>{u.role}</ManageTag>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </ManageSection>
  );
}

function TeamEditor({
  users,
  onChange,
  onDone,
}: {
  users: TeamUser[];
  onChange: (users: TeamUser[]) => void;
  onDone: () => void;
}) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('Bank Officer');

  return (
    <div>
      <DemoEraNote>
        User management has no admin backend yet — changes here are{' '}
        <strong>session-local only</strong> and are not saved. Real user provisioning ships with
        SCRUM-103.
      </DemoEraNote>
      <div className="mt-3 flex flex-col gap-2">
        {users.map((u, i) => (
          <div
            key={`${u.email}-${String(i)}`}
            className="border-border-0 bg-bg-2 rounded-tk-md flex items-center justify-between border px-3 py-2"
          >
            <span className="text-text-0 text-[0.8125rem]">
              {u.displayName}{' '}
              <span className="text-text-3 text-xs">
                · {u.email} · {u.role}
              </span>
            </span>
            <button
              type="button"
              aria-label={`Remove ${u.displayName}`}
              onClick={() => {
                onChange(users.filter((_, j) => j !== i));
              }}
              className="text-text-3 hover:text-danger text-xs"
            >
              Remove
            </button>
          </div>
        ))}
      </div>
      <div className="mt-3 grid grid-cols-1 gap-2 md:grid-cols-[1fr_1fr_140px_auto]">
        <input
          aria-label="New user name"
          value={name}
          placeholder="Full name"
          onChange={(e) => {
            setName(e.target.value);
          }}
          className={inputCls}
        />
        <input
          aria-label="New user email"
          type="email"
          value={email}
          placeholder="email@bank.com"
          onChange={(e) => {
            setEmail(e.target.value);
          }}
          className={inputCls}
        />
        <select
          aria-label="New user role"
          value={role}
          onChange={(e) => {
            setRole(e.target.value);
          }}
          className={inputCls}
        >
          {BANK_ASSIGNABLE_ROLES.map((r) => (
            <option key={r}>{r}</option>
          ))}
        </select>
        <button
          type="button"
          onClick={() => {
            if (!name.trim() || !email.trim()) return;
            onChange([...users, { displayName: name.trim(), email: email.trim(), role }]);
            setName('');
            setEmail('');
          }}
          className="border-border-1 bg-bg-3 text-text-1 hover:text-text-0 rounded-tk-md h-9 shrink-0 border px-3 text-xs font-medium"
        >
          Add User
        </button>
      </div>
      <EditorFooter doneLabel="Done" onCancel={onDone} onSave={onDone} />
    </div>
  );
}

/* ---------------------------- Section 3: Intelligence Triggers (persist) ---------------------------- */

function TriggersSection({
  tenantId,
  intelligence,
}: {
  tenantId: string;
  intelligence: TriggerConfig;
}) {
  const { showToast } = useToast();
  const update = useUpdateTenantConfig(tenantId);

  return (
    <ManageSection
      title="Intelligence Triggers"
      subtitle="Threshold configuration for the intelligence engine"
      editContent={(close) => (
        <TriggersEditor
          intelligence={intelligence}
          pending={update.isPending}
          onSave={(next) => {
            update.mutate(
              { intelligenceJson: serializeIntelligence(next) },
              {
                onSuccess: () => {
                  showToast({
                    title: 'Saved',
                    body: 'Trigger thresholds updated.',
                    variant: 'success',
                  });
                  close();
                },
                onError: (e) => {
                  showToast({ title: 'Save failed', body: e.message });
                },
              },
            );
          }}
          onCancel={close}
        />
      )}
    >
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        {TRIGGER_CATALOG.map((t) => (
          <div key={t.key} className="border-border-0 bg-bg-2 rounded-tk-md border p-3">
            <div className="text-text-0 mb-2 font-mono text-[0.8125rem] font-semibold">
              {t.name}
            </div>
            {t.fields.map((f) => (
              <div key={f.key} className="text-text-2 flex justify-between py-0.5 text-xs">
                <span>{f.label}</span>
                <span className="text-text-0 font-mono">
                  {formatTriggerValue(f.key, intelligence[t.key]?.[f.key] ?? '')}
                </span>
              </div>
            ))}
          </div>
        ))}
      </div>
    </ManageSection>
  );
}

function TriggersEditor({
  intelligence,
  pending,
  onSave,
  onCancel,
}: {
  intelligence: TriggerConfig;
  pending: boolean;
  onSave: (next: TriggerConfig) => void;
  onCancel: () => void;
}) {
  const [draft, setDraft] = useState<TriggerConfig>(intelligence);

  return (
    <div>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        {TRIGGER_CATALOG.map((t) => (
          <div key={t.key} className="border-border-0 bg-bg-2 rounded-tk-md border p-3">
            <div className="text-text-0 mb-2 font-mono text-[0.8125rem] font-semibold">
              {t.name}
            </div>
            <div className="grid grid-cols-2 gap-3">
              {t.fields.map((f) => (
                <Field key={f.key} label={f.label}>
                  <input
                    aria-label={`${t.name} ${f.label}`}
                    value={draft[t.key]?.[f.key] ?? ''}
                    onChange={(e) => {
                      setDraft({
                        ...draft,
                        [t.key]: { ...draft[t.key], [f.key]: e.target.value },
                      });
                    }}
                    className={inputCls}
                  />
                </Field>
              ))}
            </div>
          </div>
        ))}
      </div>
      <EditorFooter
        pending={pending}
        onCancel={onCancel}
        onSave={() => {
          onSave(draft);
        }}
      />
    </div>
  );
}

/* ---------------------------- Section 5: Products (persist) ---------------------------- */

function ProductsSection({
  tenantId,
  products,
}: {
  tenantId: string;
  products: OnboardingProduct[];
}) {
  const { showToast } = useToast();
  const update = useUpdateTenantConfig(tenantId);
  const subtitle = `${String(products.length)} ${products.length === 1 ? 'product' : 'products'} configured (ranked by priority)`;

  return (
    <ManageSection
      title="Products"
      subtitle={subtitle}
      editContent={(close) => (
        <ProductsEditor
          products={products}
          pending={update.isPending}
          onSave={(next) => {
            update.mutate(
              { productsJson: serializeProducts(next) },
              {
                onSuccess: () => {
                  showToast({ title: 'Saved', body: 'Products updated.', variant: 'success' });
                  close();
                },
                onError: (e) => {
                  showToast({ title: 'Save failed', body: e.message });
                },
              },
            );
          }}
          onCancel={close}
        />
      )}
    >
      {products.length === 0 ? (
        <EmptyState>No products configured for this account yet.</EmptyState>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-[0.8125rem]">
            <thead>
              <tr>
                <ManageTh width="32px">#</ManageTh>
                <ManageTh>Product</ManageTh>
                <ManageTh>Category</ManageTh>
                <ManageTh>Eligibility Rules</ManageTh>
              </tr>
            </thead>
            <tbody>
              {products.map((p, i) => (
                <tr
                  key={`${p.name}-${String(i)}`}
                  className="border-border-0 border-b last:border-0"
                >
                  <td className="text-text-3 py-2 font-mono font-semibold">{i + 1}</td>
                  <td className="text-text-0 py-2 font-medium">{p.name}</td>
                  <td className="py-2">
                    <ManageTag>{p.category}</ManageTag>
                  </td>
                  <td className="text-text-2 py-2 font-mono text-xs">
                    {String(p.rules.length)} {p.rules.length === 1 ? 'rule' : 'rules'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </ManageSection>
  );
}

function ProductsEditor({
  products,
  pending,
  onSave,
  onCancel,
}: {
  products: OnboardingProduct[];
  pending: boolean;
  onSave: (next: OnboardingProduct[]) => void;
  onCancel: () => void;
}) {
  const [draft, setDraft] = useState<OnboardingProduct[]>(products);

  return (
    <div>
      <div className="flex flex-col gap-3">
        {draft.map((p, i) => (
          <div key={i} className="border-border-0 bg-bg-2 rounded-tk-md border p-3">
            <div className="mb-2 flex items-start gap-2">
              <div className="grid flex-1 grid-cols-[1fr_140px] gap-2">
                <input
                  aria-label={`Product ${String(i + 1)} name`}
                  value={p.name}
                  onChange={(e) => {
                    setDraft(draft.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)));
                  }}
                  className={`${inputCls} font-medium`}
                />
                <select
                  aria-label={`Product ${String(i + 1)} category`}
                  value={p.category}
                  onChange={(e) => {
                    setDraft(
                      draft.map((x, j) => (j === i ? { ...x, category: e.target.value } : x)),
                    );
                  }}
                  className={inputCls}
                >
                  {PRODUCT_CATEGORIES.map((c) => (
                    <option key={c}>{c}</option>
                  ))}
                </select>
              </div>
              <button
                type="button"
                aria-label={`Remove product ${String(i + 1)}`}
                onClick={() => {
                  setDraft(draft.filter((_, j) => j !== i));
                }}
                className="text-text-3 hover:text-danger mt-2 text-xs"
              >
                Remove
              </button>
            </div>
            <textarea
              aria-label={`Product ${String(i + 1)} summary`}
              value={p.description}
              rows={2}
              placeholder="Brief description for outreach communications..."
              onChange={(e) => {
                setDraft(
                  draft.map((x, j) => (j === i ? { ...x, description: e.target.value } : x)),
                );
              }}
              className="border-border-1 bg-bg-3 text-text-0 rounded-tk-md focus:border-brand-primary mb-2 w-full resize-y border px-3 py-2 text-xs outline-none"
            />
            <EligibilityBuilder
              idPrefix={`product-${String(i)}`}
              rules={p.rules}
              onChange={(rules) => {
                setDraft(draft.map((x, j) => (j === i ? { ...x, rules } : x)));
              }}
            />
          </div>
        ))}
        <button
          type="button"
          onClick={() => {
            setDraft([
              ...draft,
              {
                name: 'New Product',
                category: 'Deposits',
                description: '',
                rules: [newRule()],
                requiredMatches: 1,
              },
            ]);
          }}
          className="border-border-1 text-text-2 hover:text-brand-primary hover:border-brand-primary rounded-tk-md self-start border border-dashed px-4 py-2 text-xs font-medium transition-colors"
        >
          Add Product
        </button>
      </div>
      <EditorFooter
        pending={pending}
        onCancel={onCancel}
        onSave={() => {
          onSave(draft);
        }}
      />
    </div>
  );
}

/* ---------------------------- Section 6: Vault (session-local) ---------------------------- */

function VaultSection({ vault }: { vault: VaultDocument[] }) {
  // Session-local demo-era editor state (plan RT1: no admin vault-mutation backend).
  const [docs, setDocs] = useState<VaultDocument[]>(vault);
  useEffect(() => {
    setDocs(vault);
  }, [vault]);

  const subtitle = `${String(docs.length)} ${docs.length === 1 ? 'document' : 'documents'} uploaded`;

  return (
    <ManageSection
      title="Vault"
      subtitle={subtitle}
      editContent={(close) => <VaultEditor docs={docs} onChange={setDocs} onDone={close} />}
    >
      {docs.length === 0 ? (
        <EmptyState>
          No documents in this account&rsquo;s Vault — files appear once the tenant is provisioned
          and documents are uploaded.
        </EmptyState>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-[0.8125rem]">
            <thead>
              <tr>
                <ManageTh>File</ManageTh>
                <ManageTh>Size</ManageTh>
                <ManageTh>Uploaded</ManageTh>
                <ManageTh>Uploaded by</ManageTh>
              </tr>
            </thead>
            <tbody>
              {docs.map((d, i) => (
                <tr
                  key={`${d.fileName}-${String(i)}`}
                  className="border-border-0 border-b last:border-0"
                >
                  <td className="text-text-0 py-2 font-medium">{d.fileName}</td>
                  <td className="text-text-2 py-2 font-mono text-xs">{formatBytes(d.sizeBytes)}</td>
                  <td className="text-text-2 py-2 text-xs">{formatUploadedDate(d.createdAt)}</td>
                  <td className="text-text-2 py-2 text-xs">{d.uploadedBy ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </ManageSection>
  );
}

function VaultEditor({
  docs,
  onChange,
  onDone,
}: {
  docs: VaultDocument[];
  onChange: (docs: VaultDocument[]) => void;
  onDone: () => void;
}) {
  return (
    <div>
      <DemoEraNote>
        Vault document management has no admin backend yet — removals here are{' '}
        <strong>session-local only</strong> and are not saved. Real document management is on the
        tenant Vault page (SCRUM-373).
      </DemoEraNote>
      {docs.length === 0 ? (
        <EmptyState>No documents to manage.</EmptyState>
      ) : (
        <div className="mt-3 flex flex-col gap-2">
          {docs.map((d, i) => (
            <div
              key={`${d.fileName}-${String(i)}`}
              className="border-border-0 bg-bg-2 rounded-tk-md flex items-center justify-between border px-3 py-2"
            >
              <span className="text-text-0 text-[0.8125rem]">
                {d.fileName}{' '}
                <span className="text-text-3 text-xs">· {formatBytes(d.sizeBytes)}</span>
              </span>
              <button
                type="button"
                aria-label={`Remove ${d.fileName}`}
                onClick={() => {
                  onChange(docs.filter((_, j) => j !== i));
                }}
                className="text-text-3 hover:text-danger text-xs"
              >
                Remove
              </button>
            </div>
          ))}
        </div>
      )}
      <EditorFooter doneLabel="Done" onCancel={onDone} onSave={onDone} />
    </div>
  );
}

/* ---------------------------- Close Account (danger) ---------------------------- */

function CloseAccountPanel({
  tenantId,
  bankName,
  onClosed,
}: {
  tenantId: string;
  bankName: string;
  onClosed: () => void;
}) {
  const { showToast } = useToast();
  const closeMutation = useCloseTenant();
  const [panelOpen, setPanelOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [typed, setTyped] = useState('');
  const phrase = useMemo(() => makeConfirmPhrase(bankName), [bankName]);
  const confirmed = typed === phrase && reason !== '';

  const submitClose = () => {
    closeMutation.mutate(
      { id: tenantId, reason, notes },
      {
        onSuccess: () => {
          showToast({ title: 'Account closed', body: `${bankName} is now closed.` });
          onClosed();
        },
        onError: (e) => {
          showToast({ title: 'Close failed', body: e.message });
        },
      },
    );
  };

  return (
    <>
      <div className="border-border-0 mt-8 flex items-center justify-between border-t pt-4">
        <span className="text-text-3 text-xs">Need to deactivate this account?</span>
        {!panelOpen && (
          <button
            type="button"
            onClick={() => {
              setPanelOpen(true);
            }}
            className="text-text-3 hover:text-danger text-xs underline decoration-dotted underline-offset-4 transition-colors"
          >
            Close account
          </button>
        )}
      </div>

      {panelOpen && (
        <div
          role="region"
          aria-label="Close Account"
          className="border-danger/20 bg-danger/5 rounded-tk-lg mt-3 border p-4"
        >
          <h3 className="font-display text-text-0 text-sm font-semibold">Close Account</h3>
          <p className="text-text-2 mb-4 text-xs">
            This will permanently deactivate the account and revoke all user access. This action
            cannot be undone.
          </p>
          <div className="mb-3 max-w-[420px]">
            <label className="text-text-2 mb-1 block text-xs font-medium" htmlFor="close-reason">
              Reason for closure *
            </label>
            <select
              id="close-reason"
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
              }}
              className="border-border-1 bg-bg-3 text-text-0 rounded-tk-md h-9 w-full border px-3 text-[0.8125rem] outline-none"
            >
              <option value="">Select reason...</option>
              {CLOSE_REASONS.map((r) => (
                <option key={r}>{r}</option>
              ))}
            </select>
          </div>
          <div className="mb-3 max-w-[420px]">
            <label className="text-text-2 mb-1 block text-xs font-medium" htmlFor="close-notes">
              Additional notes
            </label>
            <textarea
              id="close-notes"
              rows={2}
              value={notes}
              placeholder="Optional context..."
              onChange={(e) => {
                setNotes(e.target.value);
              }}
              className="border-border-1 bg-bg-3 text-text-0 rounded-tk-md w-full resize-y border px-3 py-2 text-xs outline-none"
            />
          </div>
          <div className="mb-4 max-w-[420px]">
            <label className="text-text-2 mb-1 block text-xs font-medium" htmlFor="close-phrase">
              Type this phrase to confirm: <span className="text-danger font-mono">{phrase}</span>
            </label>
            <input
              id="close-phrase"
              value={typed}
              autoComplete="off"
              spellCheck={false}
              onChange={(e) => {
                setTyped(e.target.value);
              }}
              className="border-border-1 bg-bg-3 text-text-0 rounded-tk-md h-9 w-full border px-3 font-mono text-[0.8125rem] outline-none"
            />
          </div>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => {
                setPanelOpen(false);
                setTyped('');
                setReason('');
              }}
              className="border-border-1 bg-bg-3 text-text-1 hover:text-text-0 rounded-tk-md border px-4 py-2 text-[0.8125rem] font-medium"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={!confirmed || closeMutation.isPending}
              onClick={submitClose}
              className="bg-danger rounded-tk-md px-4 py-2 text-[0.8125rem] font-semibold text-white disabled:cursor-not-allowed disabled:opacity-40"
            >
              Confirm Closure
            </button>
          </div>
        </div>
      )}
    </>
  );
}

/* ---------------------------- Shared primitives ---------------------------- */

const inputCls =
  'border-border-1 bg-bg-3 text-text-0 rounded-tk-md focus:border-brand-primary h-9 w-full border px-3 text-[0.8125rem] outline-none';

function ReadField({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-1">
      <dt className="text-text-3 font-mono text-[0.6875rem] font-semibold tracking-[0.03em] uppercase">
        {label}
      </dt>
      <dd className="text-text-0 text-[0.875rem]">{value}</dd>
    </div>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <label className="text-text-2 mb-1 block text-xs font-medium">{label}</label>
      {children}
    </div>
  );
}

function ManageTh({ children, width }: { children: ReactNode; width?: string }) {
  return (
    <th
      style={width ? { width } : undefined}
      className="border-border-0 text-text-3 border-b pb-2 text-left font-mono text-[0.6875rem] font-semibold tracking-[0.03em] uppercase"
    >
      {children}
    </th>
  );
}

function ManageTag({ children }: { children: ReactNode }) {
  return (
    <span className="border-border-0 bg-bg-2 text-text-1 rounded-tk-sm inline-block border px-2 py-0.5 font-mono text-[0.6875rem] font-semibold">
      {children}
    </span>
  );
}

function EmptyState({ children }: { children: ReactNode }) {
  return <p className="text-text-3 py-4 text-xs">{children}</p>;
}

function DemoEraNote({ children }: { children: ReactNode }) {
  return (
    <div className="rounded-tk-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-600 dark:text-amber-400">
      {children}
    </div>
  );
}

function EditorFooter({
  pending,
  onCancel,
  onSave,
  doneLabel,
}: {
  pending?: boolean;
  onCancel: () => void;
  onSave: () => void;
  /** When set, renders a single "Done" affordance (session-local editors have no Save). */
  doneLabel?: string;
}) {
  if (doneLabel) {
    return (
      <div className="border-border-0 mt-4 flex justify-end border-t pt-3">
        <button
          type="button"
          onClick={onSave}
          className="border-border-1 bg-bg-3 text-text-1 hover:text-text-0 rounded-tk-md border px-4 py-2 text-[0.8125rem] font-medium"
        >
          {doneLabel}
        </button>
      </div>
    );
  }
  return (
    <div className="border-border-0 mt-4 flex justify-end gap-2 border-t pt-3">
      <button
        type="button"
        onClick={onCancel}
        className="border-border-1 bg-bg-3 text-text-1 hover:text-text-0 rounded-tk-md border px-4 py-2 text-[0.8125rem] font-medium"
      >
        Cancel
      </button>
      <button
        type="button"
        disabled={pending}
        onClick={onSave}
        className="bg-brand-primary text-bg-0 rounded-tk-md px-4 py-2 text-[0.8125rem] font-semibold disabled:cursor-not-allowed disabled:opacity-40"
      >
        {pending ? 'Saving…' : 'Save'}
      </button>
    </div>
  );
}

function PencilIcon() {
  return (
    <svg
      viewBox="0 0 16 16"
      width="15"
      height="15"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.25"
      strokeLinecap="round"
      aria-hidden="true"
    >
      <path d="M11.5 2.5l2 2M5 9l-1 3 3-1 7.5-7.5-2-2L5 9z" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg
      viewBox="0 0 16 16"
      width="15"
      height="15"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      aria-hidden="true"
    >
      <line x1="4" y1="4" x2="12" y2="12" />
      <line x1="12" y1="4" x2="4" y2="12" />
    </svg>
  );
}
